package org.ttt.autogenesis.fleetcontrol.client

import io.ktor.client.HttpClient
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.ttt.autogenesis.fleetcontrol.protocol.ActivitySignalRequest
import org.ttt.autogenesis.fleetcontrol.protocol.EnsureFleetReadyResponse
import org.ttt.autogenesis.fleetcontrol.protocol.FleetControlRoutes
import org.ttt.autogenesis.fleetcontrol.protocol.FleetSweepRequest
import org.ttt.autogenesis.fleetcontrol.protocol.OrphanedGameSessionCleanupIntent
import org.ttt.autogenesis.fleetcontrol.protocol.SessionCleanupAckRequest
import org.ttt.autogenesis.fleetcontrol.protocol.SessionCleanupListResponse

private val fleetControlJson = Json {
    encodeDefaults = true
    ignoreUnknownKeys = true
}

/**
 * Reports that the Fleet Control API could not be called successfully.
 *
 * @property statusCode HTTP status returned by the control plane.
 */
class FleetControlRequestException(
    val statusCode: Int,
) : IllegalStateException("Fleet Control request failed with HTTP status $statusCode")

/**
 * Reports that an embedding application could not supply a bearer token.
 */
class FleetControlAuthenticationException : IllegalStateException("Fleet Control bearer token was unavailable")

/**
 * Common HTTP client for public readiness and internal activity-signal operations.
 *
 * The token provider is supplied by the embedding application so this module never
 * stores user credentials, service credentials, or access tokens.
 *
 * @param baseUrl Fleet Control API base URL.
 * @param bearerTokenProvider Suspended provider for the current bearer token.
 * @param httpClient Optional platform-configured client for tests or shared ownership.
 */
class FleetControlApiClient(
    baseUrl: String,
    private val bearerTokenProvider: suspend () -> String?,
    httpClient: HttpClient? = null,
)
{
    private val normalizedBaseUrl: String = baseUrl.trimEnd('/')
    private val httpClient: HttpClient
    private val ownsHttpClient: Boolean

    init
    {
        require(normalizedBaseUrl.isNotBlank()) { "baseUrl cannot be blank" }
        if(httpClient == null)
        {
            this.httpClient = HttpClient()
            ownsHttpClient = true
        }
        else
        {
            this.httpClient = httpClient
            ownsHttpClient = false
        }
    }

    /**
     * Records client demand and returns the current fleet readiness state.
     *
     * @return Readiness response returned by the control plane.
     */
    suspend fun ensureReady(): EnsureFleetReadyResponse
    {
        val response = post(FleetControlRoutes.ENSURE_READY)
        requireSuccessfulStatus(response, setOf(HttpStatusCode.OK.value, HttpStatusCode.Accepted.value))
        return fleetControlJson.decodeFromString(response.bodyAsText())
    }

    /** Ensures the server-extend role and returns its endpoint lease. */
    suspend fun ensureServerExtendReady(): EnsureFleetReadyResponse
    {
        val route = if(isInternalFleetControlCaller())
        {
            FleetControlRoutes.ENSURE_SERVER_EXTEND_ENDPOINT
        }
        else
        {
            FleetControlRoutes.ENSURE_SERVER_EXTEND_READY
        }
        val response = post(route)
        requireSuccessfulStatus(response, setOf(HttpStatusCode.OK.value, HttpStatusCode.Accepted.value))
        return fleetControlJson.decodeFromString(response.bodyAsText())
    }

    /** Ensures the server-extend role for an internal game-server caller. */
    suspend fun ensureServerExtendEndpoint(): EnsureFleetReadyResponse
    {
        val response = post(FleetControlRoutes.ENSURE_SERVER_EXTEND_ENDPOINT)
        requireSuccessfulStatus(response, setOf(HttpStatusCode.OK.value, HttpStatusCode.Accepted.value))
        return fleetControlJson.decodeFromString(response.bodyAsText())
    }

    /** Ensures the game-server role before Match2 starts. */
    suspend fun ensureGameServerReady(): EnsureFleetReadyResponse
    {
        val response = post(FleetControlRoutes.ENSURE_GAME_SERVER_READY)
        requireSuccessfulStatus(response, setOf(HttpStatusCode.OK.value, HttpStatusCode.Accepted.value))
        return fleetControlJson.decodeFromString(response.bodyAsText())
    }

    /** Requests a scoped, idempotent reconciliation sweep. */
    suspend fun sweep(request: FleetSweepRequest)
    {
        val response = post(
            path = FleetControlRoutes.FLEET_SWEEP,
            body = fleetControlJson.encodeToString(request),
        )
        requireSuccessfulStatus(response, (200..299).toSet())
    }

    /**
     * Sends server or dedicated-server activity telemetry to the control plane.
     *
     * @param request Activity telemetry to persist.
     */
    suspend fun sendActivitySignal(request: ActivitySignalRequest)
    {
        val response = post(
            path = FleetControlRoutes.ACTIVITY_SIGNAL,
            body = fleetControlJson.encodeToString(request),
        )
        requireSuccessfulStatus(response, (200..299).toSet())
    }

    /** Polls durable game-session cleanup work after Fleet Control observed an absent AMS DS. */
    suspend fun listPendingSessionCleanups(): List<OrphanedGameSessionCleanupIntent>
    {
        val response = post(FleetControlRoutes.LIST_SESSION_CLEANUPS)
        requireSuccessfulStatus(response, setOf(HttpStatusCode.OK.value))
        return fleetControlJson.decodeFromString<SessionCleanupListResponse>(response.bodyAsText()).intents
    }

    /** Acknowledges the exact cleanup only after AGS confirms the Match2 session is gone. */
    suspend fun acknowledgeSessionCleanup(intentId: String, dsId: String, gameSessionId: String)
    {
        val response = post(
            path = FleetControlRoutes.ACK_SESSION_CLEANUP,
            body = fleetControlJson.encodeToString(SessionCleanupAckRequest(intentId, dsId, gameSessionId)),
        )
        requireSuccessfulStatus(response, setOf(HttpStatusCode.NoContent.value))
    }

    /**
     * Releases the internally-owned HTTP client, if this instance created one.
     */
    fun close()
    {
        if(ownsHttpClient)
        {
            httpClient.close()
        }
    }

    private suspend fun post(path: String, body: String? = null): HttpResponse
    {
        val token = bearerTokenProvider()?.trim()?.takeIf { it.isNotEmpty() }
            ?: throw FleetControlAuthenticationException()
        val requestBody = body
        return httpClient.post(normalizedBaseUrl + path)
        {
            header(HttpHeaders.Authorization, fleetControlAuthorizationHeaderValue(token))
            contentType(ContentType.Application.Json)
            if(requestBody != null)
            {
                setBody(requestBody)
            }
        }
    }

    private fun requireSuccessfulStatus(response: HttpResponse, acceptedStatuses: Set<Int>)
    {
        if(response.status.value !in acceptedStatuses)
        {
            throw FleetControlRequestException(response.status.value)
        }
    }

}

internal expect fun isInternalFleetControlCaller(): Boolean
